import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { deleteAccount } from '@/lib/api/account';
import { getAccountError } from '@/lib/errors';
import type { FriendlyError } from '@/lib/errors';
import type { ApiError } from '@/lib/api/client';
import { captureFriendlyError } from '@/lib/sentry';
import { AUTH_ENABLED } from '@/lib/prelaunch';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import ErrorAlert from '@/components/ui/ErrorAlert';
import Spinner from '@/components/ui/Spinner';

interface DeleteAccountModalProps {
  visible: boolean;
  onClose: () => void;
}

export default function DeleteAccountModal({ visible, onClose }: DeleteAccountModalProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const userEmail = user?.email ?? '';
  const matches =
    !!userEmail && confirmEmail.trim().toLowerCase() === userEmail.toLowerCase();

  const close = () => {
    if (submitting) return;
    setConfirmEmail('');
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!matches || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteAccount();
      await signOut();
      navigate(AUTH_ENABLED ? '/auth' : '/', { replace: true });
    } catch (err) {
      const apiError = err as ApiError;
      const friendly = getAccountError(apiError.code || 'delete_failed');
      captureFriendlyError(err, friendly);
      setError(friendly);
      setSubmitting(false);
    }
  };

  return (
    <Modal visible={visible} onClose={close}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-error-light flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={20} className="text-error" />
        </div>
        <div>
          <p className="font-display font-semibold text-xl text-text-primary leading-tight">
            Delete your account?
          </p>
          <p className="font-sans text-sm text-text-secondary mt-1">
            This can't be undone.
          </p>
        </div>
      </div>

      <ul className="font-sans text-sm text-text-secondary space-y-1.5 mb-5 list-disc list-inside">
        <li>Your profile and study history will be permanently deleted.</li>
        <li>Any active subscription will be canceled.</li>
        <li>You'll be signed out and your data won't be recoverable.</li>
      </ul>

      <p className="font-sans text-sm text-text-secondary mb-2">
        Type your email{' '}
        <span className="font-mono text-text-primary">{userEmail}</span> to confirm:
      </p>
      <Input
        value={confirmEmail}
        onChange={setConfirmEmail}
        placeholder={userEmail}
        type="email"
        disabled={submitting}
      />

      {error && <ErrorAlert error={error} className="mt-4" />}

      <div className="mt-5 flex gap-3">
        <button
          type="button"
          onClick={close}
          disabled={submitting}
          className="flex-1 py-3 rounded-xl font-sans font-semibold text-base text-text-secondary border-2 border-border bg-transparent hover:border-text-muted transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!matches || submitting}
          className="flex-1 py-3 rounded-xl font-sans font-semibold text-base text-white bg-error hover:bg-error/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          {submitting ? <Spinner /> : 'Delete account'}
        </button>
      </div>
    </Modal>
  );
}
